/**
 * @fileoverview A parent's children, and one child's lessons (F1).
 *
 * Two small pages rather than one, because a parent with three children on
 * two teachers needs to choose before they read.
 *
 * The empty state is the one that matters. A parent whose instructor has not
 * put their address on the roster yet will land here with nothing, and that
 * is a perfectly ordinary situation rather than a failure — so it says what
 * to do about it, and names the address we matched on, which is almost always
 * where the mismatch is.
 */

import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import parentApi from "@/lib/parentApi";
import { useParentAuth } from "@/contexts/ParentAuthContext";
import { Card, SectionHeader, ListRow, Tag, Button } from "@/components/studio/primitives";
import RichText from "@/components/RichText";
import { ChildAccessCard } from "@/pages/parent/ChildModePage";
import FamilyPlanCard from "@/components/FamilyPlanCard";
import CalendarSyncSection from "@/components/settings/CalendarSyncSection";
import { calendarSyncFlags } from "@/lib/featureFlags";

function formatDate(value) {
  const when = new Date(value);
  return Number.isNaN(when.getTime())
    ? ""
    : when.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

export function ParentDashboard() {
  const { parent, flags, logout } = useParentAuth();
  const [children, setChildren] = useState(null);

  useEffect(() => {
    parentApi
      .get("/parent/children")
      .then(({ data }) => setChildren(data.children || []))
      .catch(() => setChildren([]));
  }, []);

  if (!children) return null;

  return (
    <div className="mx-auto max-w-[840px] px-6 py-11">
      <div className="mb-6 flex items-start justify-between">
        <div>
          <h1 className="font-serif text-3xl text-[var(--text-primary)]">Your family</h1>
          <p className="mt-1 font-sans text-[14px] text-[var(--text-tertiary)]">{parent?.email}</p>
        </div>
        <Button variant="ghost" onPress={logout} data-testid="parent-logout">
          Sign out
        </Button>
      </div>

      {/* The household plan (F2) — above the children, because it is about
          the family rather than any one of them. Hides itself when there is
          nothing to sell and nothing held. */}
      <FamilyPlanCard />

      {children.length === 0 ? (
        <Card data-testid="parent-empty">
          <SectionHeader>Nothing here yet</SectionHeader>
          <p className="font-sans text-[14px] text-[var(--text-secondary)]">
            We match you to your children by the parent email their teacher has on file. Nothing is listed for{" "}
            <span className="font-mono text-[13px] text-[var(--text-primary)]">{parent?.email}</span> yet — ask their
            teacher to check the address on your child&rsquo;s record.
          </p>
        </Card>
      ) : (
        <Card>
          <SectionHeader>Children</SectionHeader>
          <div className="-mx-[18px]">
            {children.map((c, i) => (
              <Link key={c.student_id} to={`/parent/children/${c.student_id}`} className="block">
                <ListRow
                  borderBottom={i < children.length - 1}
                  title={`${c.first_name} ${c.last_name}`.trim()}
                  subtitle={`${c.teacher_name}${c.instruments?.length ? ` · ${c.instruments.join(", ")}` : ""}`}
                  trailing={c.consent_status === "granted" ? null : <Tag>{c.consent_status}</Tag>}
                />
              </Link>
            ))}
          </div>
        </Card>
      )}
      {children.length > 0 && (
        <CalendarSyncSection
          portal="parent"
          client={parentApi}
          prefix="/parent"
          available={calendarSyncFlags({ flags })}
          className="mt-8"
        />
      )}
    </div>
  );
}

export function ParentChildPage() {
  const { studentId } = useParams();
  const [data, setData] = useState(null);
  const [openId, setOpenId] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await parentApi.get(`/parent/children/${studentId}/lessons`);
      setData(res.data);
    } catch {
      setData({ child: null, lessons: [] });
    }
  }, [studentId]);

  useEffect(() => {
    load();
  }, [load]);

  if (!data) return null;

  return (
    <div className="mx-auto max-w-[840px] px-6 py-11">
      <Link to="/parent" className="font-sans text-[13px] text-[var(--text-tertiary)] underline">
        ← Your family
      </Link>
      <h1 className="mb-1 mt-3 font-serif text-3xl text-[var(--text-primary)]">
        {data.child ? `${data.child.first_name}'s lessons` : "Lessons"}
      </h1>
      {/* Said outright, because it is the whole shape of what this portal is:
          a durable view of the summaries already in their inbox. */}
      <p className="mb-6 font-sans text-[14px] text-[var(--text-tertiary)]">
        Every summary their teacher has sent you, kept in one place.
      </p>

      {/* Child mode (F10) — the parent's switch for their child's own view. */}
      {data.child ? <ChildAccessCard studentId={studentId} firstName={data.child.first_name} /> : null}

      {data.lessons.length === 0 ? (
        <Card data-testid="parent-no-lessons">
          <p className="font-sans text-[14px] text-[var(--text-secondary)]">
            No summaries yet. They arrive here when their teacher sends one.
          </p>
        </Card>
      ) : (
        <Card>
          <div className="-mx-[18px]">
            {data.lessons.map((lesson, i) => (
              <div key={lesson.lesson_id}>
                <ListRow
                  borderBottom={i < data.lessons.length - 1}
                  title={lesson.title || "Lesson"}
                  subtitle={formatDate(lesson.created_at)}
                  onClick={() => setOpenId(openId === lesson.lesson_id ? null : lesson.lesson_id)}
                  data-testid={`parent-lesson-${lesson.lesson_id}`}
                />
                {openId === lesson.lesson_id ? (
                  <div className="px-[18px] pb-5" data-testid={`parent-lesson-body-${lesson.lesson_id}`}>
                    {lesson.summary ? (
                      <RichText className="font-sans text-[14px] text-[var(--text-secondary)]" value={lesson.summary} />
                    ) : null}
                    {lesson.practice_items?.length ? (
                      <ol className="mt-3 list-decimal pl-5">
                        {lesson.practice_items.map((item, n) => (
                          <li key={n} className="font-sans text-[14px] text-[var(--text-secondary)]">
                            {item}
                          </li>
                        ))}
                      </ol>
                    ) : null}
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}

export default ParentDashboard;
